import Spinner from 'parkdepot-shared/components/Spinner'
import React, { FC, useContext, useEffect, useRef, useState } from 'react'

// hooks
import useAdyenSession from '../../hooks/useAdyenSession'

// components
import Dropin from './Dropin'

// style components
import { LoadingContainer } from './Dropin.style'

// utils
import useLanguage from '@hooks/useLanguage.hook'
import { CheckoutContext } from '../../context/CheckoutContext/checkout.context'
import { CREATE_SUBSCRIPTION_SESSION } from '../../graphql/mutation/createSubsSession.mutation'
import { useAuthenticatedMutation } from '../../hooks/useAuth'
import { AdyenResultStatus } from '../../types/adyenResultStatus.enum'
import { PaymentMethod } from '../../types/checkout.interface'
import { getDomainExtension } from '../../utils/getDomainExtension'
import { getLocaleForAdyen } from '../../utils/getLocaleForAdyen.utils'

interface Props {
    onCompleted: (resultCode: AdyenResultStatus) => void
    onError: (error: any) => void
    onPaymentMethodChange: (method: PaymentMethod) => void
}

const SubscriptionDropinContainer: FC<Props> = ({
    // orderId,
    // loading,
    // email,
    onCompleted,
    onError,
    onPaymentMethodChange
}) => {
    const [session, setSession] = useState<{ [key: string]: string }>({})
    const [lng] = useLanguage()
    const dropInRef = useRef<null | HTMLDivElement>(null)
    const [dropin, setDropin] = useState<any>()
    const [checkoutError, setCheckoutError] = useState<any>()
    const [loading, setLoading] = useState(true)
    const onPaymentCompleted = (resultCode: AdyenResultStatus) => {
        onCompleted(resultCode)
    }

    const { checkout } = useContext(CheckoutContext)

    const onPaymentError = (error: any) => {
        onError(error)
    }
    const [createDropin, error] = useAdyenSession(session, onPaymentCompleted, onPaymentError)

    const [createSubSession] = useAuthenticatedMutation({
        mutation: CREATE_SUBSCRIPTION_SESSION
    })

    useEffect(() => {
        if (!checkout || !checkout.lot) return
        const licensePlateId = checkout?.selectedVehicles?.[0]?.plate_id

        const variables = {
            lot_id: checkout?.lot?.id,
            shopper_email: checkout?.email,
            shopper_locale: getLocaleForAdyen(lng),
            start_date: checkout?.start_datetime,
            top_level_domain: getDomainExtension(),
            license_plate_id: licensePlateId
        }

        const createSession = async () => {
            try {
                const response = await createSubSession({
                    variables
                })
                if (response && response.data) {
                    setSession({
                        id: response.data.booking_create_subs_session.session_id,
                        sessionData: response.data.booking_create_subs_session.session_data
                    })
                }
            } catch (error) {
                setCheckoutError(error)
            } finally {
                setLoading(false)
            }
        }

        createSession()
    }, [checkout, createSubSession, lng])

    useEffect(() => {
        if (checkoutError) {
            onError(checkoutError)
        }
    }, [checkoutError, onError])

    useEffect(() => {
        const initDropin = async () => {
            if (dropin) {
                setDropin(undefined)
            }
            const dropIn = await createDropin()
            setDropin(dropIn)
        }
        if (session?.id) {
            initDropin()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session])

    // a really hacky way to get the payment method...
    useEffect(() => {
        const handleClick = () => {
            if (dropin.activePaymentMethod) {
                setTimeout(() => {
                    onPaymentMethodChange(dropin.activePaymentMethod.type)
                }, 200)
            }
        }
        if (dropInRef && dropInRef.current) {
            dropInRef.current.addEventListener('click', handleClick)
        }
        return () => {
            dropInRef?.current?.removeEventListener('click', handleClick)
        }
    }, [dropin, onPaymentMethodChange])

    return (
        <>
            {!dropin || loading ? (
                <LoadingContainer>
                    <Spinner size="lg" theme="primary" />
                </LoadingContainer>
            ) : (
                <Dropin dropInRef={dropInRef} dropin={dropin} error={error} />
            )}
        </>
    )
}

export default SubscriptionDropinContainer
